import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cacheKeys, useFetcher } from '../../graphql';

export interface SetWorkspacePhoneNumberInput {
  workspaceId: string;
  phoneNumber: string | null;
}

export interface SetWorkspacePhoneNumberResult {
  workspaceId: string;
  phoneNumber: string | null;
}

interface SetWorkspacePhoneNumberResponse {
  workspace: {
    setWorkspacePhoneNumber: SetWorkspacePhoneNumberResult;
  };
}

/**
 * Set/clear the workspace's inbound-call phone number (Communication settings
 * — plan 0025 Phase 2). Requires global admin on the backend (`*.*`); gate the
 * UI entry point with the same permission.
 */
export const useSetWorkspacePhoneNumber = () => {
  const queryClient = useQueryClient();
  const fetcherFn = useFetcher<
    SetWorkspacePhoneNumberResponse,
    { input: SetWorkspacePhoneNumberInput }
  >(SET_WORKSPACE_PHONE_NUMBER);

  return useMutation({
    mutationFn: async (input: SetWorkspacePhoneNumberInput) => {
      const data = await fetcherFn({ input });
      return data.workspace.setWorkspacePhoneNumber;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('workspace').detail(result.workspaceId),
      });
    },
  });
};

const SET_WORKSPACE_PHONE_NUMBER = `
  mutation SetWorkspacePhoneNumber($input: SetWorkspacePhoneNumberInput!) {
    workspace {
      setWorkspacePhoneNumber(input: $input) {
        workspaceId
        phoneNumber
      }
    }
  }
`;
